import { translateToAPI } from "@/composables/profileUtils";
import { helpers } from "@/services/helpers";
import { profileService } from "@/services/internal-api/profile.service";
import { thirdPartyService } from "@/services/internal-api/thirdparty.service";
import { socialMediaService } from "@/services/socialmediaservice/socialmedia.service";
import store from "@/state/store";
import {
  KCMPPFeature,
  KCMPPValuesTable,
  KCMPPFieldType,
  KCMPPProfile,
  KCMPPLocalData,
} from "@/types/kcm-profile-api.types";
import { KcmSocialPageData } from "@/types/social-media.types";
import { ref } from "vue";

export interface KcmSocialPageOption extends KcmSocialPageData {
  enabled: boolean;
}

export type AutoposterSocialKey = "facebook" | "linkedin" | "instagram";
export type AvailablePages = {
  facebook: KcmSocialPageOption[];
  instagram: KcmSocialPageOption[];
  linkedin: KcmSocialPageOption[];
};

export async function getNetworkPages(): Promise<AvailablePages> {
  const medias: AutoposterSocialKey[] = ["facebook", "linkedin", "instagram"];
  const pageData: AvailablePages = {} as AvailablePages;

  for (let index = 0; index < medias.length; index++) {
    const mSlug = medias[index] as AutoposterSocialKey;
    if (store.getters["auth/getSocialToken"](mSlug)) {
      const service = socialMediaService.services[mSlug];
      const pageResp = await socialMediaService.getPages(service);

      if (pageResp?.success) {
        pageData[mSlug] = pageResp.data.map((page: KcmSocialPageData) => {
          if (!/^\d+$/.test(page.value)) {
            page.value = "";
          }
          return { ...page, enabled: false };
        });
      }
    }
  }

  return pageData;
}

export async function setValueMoment(
  value_moment: string,
  email: string
): Promise<void> {
  try {
    await thirdPartyService.post("/hubspot/v1/contact/" + email, {
      kcm_value_moment: value_moment,
    });
  } catch (err) {
    helpers.customSentryError(
      "Setting value moment",
      "Failed to set KCM value moment in hubspot",
      { error: err }
    );
  }
}

async function updateProfileFeature(
  profileData: KCMPPProfile,
  feature: KCMPPFeature,
  transform: boolean
): Promise<string | boolean> {
  if (transform) {
    profileData = translateToAPI(profileData, feature);
  }

  return profileService.updateProfileByFeature(
    store.getters["auth/authUserId"],
    feature,
    profileData
  );
}

export async function setPersonalization(
  profile: KCMPPProfile,
  localMarket?: KCMPPLocalData
): Promise<boolean> {
  const resp = await updateProfileFeature(profile, KCMPPFeature.BASIC, true);

  if (typeof resp === "string") {
    return false;
  }

  const requests = [];

  if (localMarket) {
    requests.push(
      updateProfileFeature(
        {
          markets: {
            fontSize: 0,
            SyncTo: "",
            value: [localMarket],
            table: KCMPPValuesTable.USER_MARKETS,
            field_type: KCMPPFieldType.ARRAY,
          },
        },
        KCMPPFeature.LOCAL,
        false
      )
    );
  }

  const featuresToCreate = [
    KCMPPFeature.BASIC,
    KCMPPFeature.BLOG,
    KCMPPFeature.GRAPHICS,
    KCMPPFeature.GUIDES,
    KCMPPFeature.NEWSLETTER,
    KCMPPFeature.VIDEO,
  ];

  requests.push(
    profileService.createDefaultProfileFields(
      store.getters["auth/authUserId"],
      featuresToCreate
    )
  );

  const results = await Promise.allSettled(requests);
  const rejected = results.filter((result) => result.status === "rejected");

  if (rejected.length) {
    return false;
  }

  return true;
}

export const hubspotFields = ref({
  kcm_onboarding_role: "",
  kcm_onboarding_tenure: "",
  kcm_onboarding_goal: "",
});

export const sendHubspotData = async () => {
  await thirdPartyService.updateHubspotContact(
    store.getters["auth/authEmail"],
    hubspotFields.value
  );
};

const funnelTypes = ["playbooks", "social", "realtalk", "standard"] as const;
export type FunnelType = (typeof funnelTypes)[number];
export const funnel = ref<FunnelType>("standard");
export function isValidFunnelType(value: string): value is FunnelType {
  return funnelTypes.includes(value as FunnelType);
}
